<template>
  <div class="login-wrapper">
    <!-- Background pattern -->
    <div class="login-bg-grid"></div>

    <!-- Tarjeta principal -->
    <div class="login-card">
      <!-- Brand header -->
      <div class="login-brand">
        <img src="/kompara.png" alt="Kompara" class="login-logo" />
        <div class="brand-meta">
          <span class="brand-label">DASHBOARD</span>
          <span class="brand-divider">/</span>
          <span class="brand-client">VSVIP</span>
        </div>
      </div>

      <!-- Status dot + sistema -->
      <div class="login-status">
        <span class="status-dot"></span>
        <span class="status-text">SISTEMA OPERATIVO</span>
      </div>

      <!-- Form -->
      <form @submit.prevent="handleLogin" class="login-form">
        <div class="form-field">
          <label class="field-label">USUARIO</label>
          <input
            v-model="username"
            type="text"
            placeholder="admin"
            required
            autofocus
            class="bb-input"
          />
        </div>
        <div class="form-field">
          <label class="field-label">CONTRASEÑA</label>
          <input
            v-model="password"
            type="password"
            placeholder="••••••••"
            required
            class="bb-input"
          />
        </div>

        <button type="submit" :disabled="loading" class="btn-login">
          <span v-if="!loading">INICIAR SESIÓN →</span>
          <span v-else>VALIDANDO…</span>
        </button>

        <p v-if="errorMsg" class="error-text">
          <PhWarning :size="13" weight="bold" /> {{ errorMsg }}
        </p>
      </form>

      <!-- Footer -->
      <div class="login-footer">
        <span class="footer-label">v5.0</span>
        <span class="footer-sep">·</span>
        <span class="footer-text">Kompara Intelligence Platform</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import axios from 'axios';
import { PhWarning } from '@phosphor-icons/vue';

const emit = defineEmits(['authenticated']);

const username = ref('');
const password = ref('');
const loading = ref(false);
const errorMsg = ref('');

const handleLogin = async () => {
  errorMsg.value = '';
  loading.value = true;
  try {
    const params = new URLSearchParams();
    params.append('username', username.value);
    params.append('password', password.value);
    const response = await axios.post(
      'https://dash-visaovip.kompara.tech/api/auth/login',
      params
    );
    if (response.data.access_token) {
      localStorage.setItem('token_precios', response.data.access_token);
      emit('authenticated', true);
    }
  } catch (err) {
    errorMsg.value = err?.response?.data?.detail || 'Credenciales inválidas';
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.login-wrapper {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: var(--color-background);
  overflow: hidden;
  font-family: 'Inter', sans-serif;
}

/* Background grid (Bloomberg style) */
.login-bg-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(var(--color-border) 1px, transparent 1px),
    linear-gradient(90deg, var(--color-border) 1px, transparent 1px);
  background-size: 32px 32px;
  opacity: 0.4;
  mask-image: radial-gradient(ellipse at center, black 0%, transparent 70%);
  -webkit-mask-image: radial-gradient(ellipse at center, black 0%, transparent 70%);
}

/* Card */
.login-card {
  position: relative;
  background: var(--color-surface);
  border: 1px solid var(--color-accent-border);
  border-radius: 4px;
  padding: 32px 28px;
  width: 100%;
  max-width: 380px;
  box-shadow: var(--shadow-lg);
  z-index: 1;
}

/* Brand */
.login-brand {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}
.login-logo {
  max-width: 160px;
  height: auto;
  display: block;
}
.brand-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}
.brand-label {
  color: var(--color-text-muted);
}
.brand-divider {
  color: var(--color-text-faint);
}
.brand-client {
  color: var(--color-accent);
}

/* Status */
.login-status {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 10px;
  background: var(--color-success-bg);
  border: 1px solid var(--color-success);
  border-radius: 2px;
  margin-bottom: 22px;
}
.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--color-success);
  box-shadow: 0 0 8px var(--color-success);
  animation: pulse 1.6s ease-in-out infinite;
}
.status-text {
  font-family: var(--font-mono);
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.15em;
  color: var(--color-success-text);
}
@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

/* Form */
.login-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.form-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.field-label {
  font-family: var(--font-mono);
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: var(--color-text-muted);
}
.bb-input {
  width: 100%;
  padding: 9px 12px;
  border-radius: 2px;
  border: 1px solid var(--color-border-strong);
  background: var(--color-background);
  color: var(--color-text);
  font-family: var(--font-mono);
  font-size: 12.5px;
  box-sizing: border-box;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.bb-input::placeholder {
  color: var(--color-text-faint);
  font-style: italic;
}
.bb-input:focus {
  outline: none;
  border-color: var(--color-accent);
  box-shadow: 0 0 0 2px var(--color-accent-bg);
}

/* Botón principal */
.btn-login {
  margin-top: 8px;
  padding: 11px 16px;
  background: var(--color-accent-bg);
  color: var(--color-accent);
  border: 1px solid var(--color-accent-border);
  border-radius: 2px;
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.15em;
  cursor: pointer;
  transition: all 0.15s;
}
.btn-login:hover:not(:disabled) {
  background: var(--color-accent);
  color: var(--color-surface-3);
  letter-spacing: 0.2em;
}
.btn-login:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

/* Error */
.error-text {
  margin: 0;
  padding: 8px 10px;
  background: var(--color-danger-bg);
  border: 1px solid var(--color-danger);
  color: var(--color-danger-text);
  font-family: var(--font-mono);
  font-size: 11px;
  border-radius: 2px;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* Footer */
.login-footer {
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid var(--color-border);
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  font-family: var(--font-mono);
  font-size: 9.5px;
  color: var(--color-text-faint);
  letter-spacing: 0.05em;
}
.footer-label {
  color: var(--color-accent);
  font-weight: 700;
}
.footer-sep {
  opacity: 0.5;
}
.footer-text {
  text-transform: uppercase;
  letter-spacing: 0.1em;
}
</style>